// اجزای مشترک صفحات کاتالوگ (AP-CAT-01): کلیدهای Query، فهرست نوع و واحد، دسته‌ها و تخصص‌ها (داده مرجع نمونه).
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import {
  unwrap,
  type CategoryView,
  type ReferenceItem,
  type ServiceStatus,
  type ServiceType,
  type ServiceUnit,
} from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { StatusChip, type StatusTone } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';

export const SERVICE_TYPES: readonly ServiceType[] = [
  'Visit',
  'Procedure',
  'Session',
  'Diagnostic',
  'Consultation',
  'ProductSale',
  'Administrative',
];

export const SERVICE_UNITS: readonly ServiceUnit[] = [
  'Each',
  'Area',
  'Unit',
  'Milliliter',
  'Shot',
  'Minute',
  'Tooth',
  'Session',
];

export const catKeys = {
  all: (tenantId: string | undefined) => ['catalog', tenantId] as const,
  categories: (tenantId: string | undefined) => ['catalog', tenantId, 'categories'] as const,
  services: (tenantId: string | undefined, filter: Record<string, unknown> = {}) =>
    ['catalog', tenantId, 'services', filter] as const,
  service: (tenantId: string | undefined, id: string) => ['catalog', tenantId, 'service', id] as const,
  durations: (tenantId: string | undefined, id: string) => ['catalog', tenantId, 'durations', id] as const,
  history: (tenantId: string | undefined, id: string) => ['catalog', tenantId, 'price-history', id] as const,
  rows: (tenantId: string | undefined, filter: Record<string, unknown> = {}) =>
    ['catalog', tenantId, 'price-rows', filter] as const,
  specialties: ['platform', 'reference-data', 'MEDICAL_SPECIALTIES'] as const,
  providers: (tenantId: string | undefined) => ['identity', tenantId, 'providers'] as const,
  setup: (tenantId: string | undefined, id: string) => ['catalog', tenantId, 'setup', id] as const,
  requirement: (tenantId: string | undefined, id: string, part: string) =>
    ['catalog', tenantId, 'requirement', id, part] as const,
  taxProfiles: (tenantId: string | undefined) => ['catalog', tenantId, 'tax-profiles'] as const,
  priceLists: (tenantId: string | undefined) => ['catalog', tenantId, 'price-lists'] as const,
  groups: (tenantId: string | undefined) => ['catalog', tenantId, 'provider-groups'] as const,
  bulk: (tenantId: string | undefined) => ['catalog', tenantId, 'bulk-price-changes'] as const,
  packages: (tenantId: string | undefined) => ['catalog', tenantId, 'packages'] as const,
  package: (tenantId: string | undefined, id: string) => ['catalog', tenantId, 'package', id] as const,
  memberships: (tenantId: string | undefined) => ['catalog', tenantId, 'membership-plans'] as const,
  discounts: (tenantId: string | undefined) => ['catalog', tenantId, 'discount-rules'] as const,
  activeServices: (tenantId: string | undefined) =>
    ['catalog', tenantId, 'services', 'active-options'] as const,
  formOptions: (tenantId: string | undefined) => ['catalog', tenantId, 'form-template-options'] as const,
  nationalCodes: (tenantId: string | undefined, q: string) =>
    ['catalog', tenantId, 'national-codes', q] as const,
  tags: (tenantId: string | undefined) => ['organization', tenantId, 'resource-tags'] as const,
  branches: (tenantId: string | undefined) => ['platform', tenantId, 'branches'] as const,
  tariffVersions: (tenantId: string | undefined) => ['catalog', tenantId, 'tariff-book-versions'] as const,
  tariffImpact: (tenantId: string | undefined, dataset: string, version: number, branchId: string) =>
    ['catalog', tenantId, 'tariff-impact', dataset, version, branchId] as const,
  tariffComparison: (tenantId: string | undefined, branchId: string) =>
    ['catalog', tenantId, 'tariff-comparison', branchId] as const,
  contracts: (tenantId: string | undefined) => ['catalog', tenantId, 'organization-contracts'] as const,
  beneficiaries: (tenantId: string | undefined, contractId: string) =>
    ['catalog', tenantId, 'contract-beneficiaries', contractId] as const,
};

export const BOOKING_CHANNELS = ['Phone', 'Portal', 'Widget', 'Kiosk'] as const;
export const PROVIDER_TYPES = [
  'Physician',
  'Dentist',
  'Therapist',
  'Technician',
  'Nurse',
  'Psychologist',
  'Nutritionist',
  'Midwife',
] as const;
/** انواع مدرک حرفه‌ای (AC-IAM-022.1) برای شرط صلاحیت (US-CAT-012). */
export const CREDENTIAL_TYPES = [
  'MedicalCouncil',
  'NursingCouncil',
  'PsychologyCouncil',
  'ClinicLicense',
  'SpecialtyCertificate',
  'CourseCertificate:laser',
  'Other',
] as const;

/** درصد نمایشی ← یک‌ده‌هزارم (۲۵ ← ۲۵۰۰؛ دو رقم اعشار). */
export function toBasisPoints(percent: number): number {
  return Math.round(percent * 100);
}

/** یک‌ده‌هزارم ← درصد نمایشی. */
export function fromBasisPoints(basisPoints: number | string | null | undefined): number | undefined {
  return basisPoints === null || basisPoints === undefined ? undefined : Number(basisPoints) / 100;
}

/** پیوند صفحه جزئیات خدمت (SCR-STAFF-201). */
export const servicePath = (id: string) => `/settings/services/${id}`;

/**
 * خدمات فعال برای انتخاب در پکیج، تخفیف و شبیه‌ساز (AP-CAT-03): جستجوی سروری با `search` (۱۰۰ نتیجه نخست) و نگه‌داشت نام خدمات دیده‌شده
 * برای نمایش مقدار انتخاب‌شده.
 */
export function useActiveServices() {
  const { tenantApi, session } = useTenantSession();
  const [search, setSearch] = useState('');
  const seen = useRef(new Map<string, string>());
  const query = useQuery({
    queryKey: [...catKeys.activeServices(session?.tenantId), search] as const,
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/catalog/services', {
          params: { query: { status: 'Active', limit: 100, q: search.trim() || undefined } },
        }),
      ),
  });
  const items = query.data?.items ?? [];
  for (const service of items) {
    seen.current.set(service.id, service.name);
  }
  return {
    ...query,
    search: setSearch,
    options: items.map((service) => ({ id: service.id, name: service.name, code: service.code })),
    name: (id: string | null | undefined) => (id ? (seen.current.get(id) ?? id) : ''),
  };
}

/** همه دسته‌ها (درخت در Client ساخته می‌شود). */
export function useCategories() {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: catKeys.categories(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/categories')),
  });
}

export interface CategoryNode {
  category: CategoryView;
  children: CategoryNode[];
}

/** درخت دسته‌ها مرتب بر ترتیب و نام. */
export function buildTree(categories: readonly CategoryView[]): CategoryNode[] {
  const byParent = new Map<string | null, CategoryView[]>();
  for (const category of categories) {
    const key = category.parentId ?? null;
    byParent.set(key, [...(byParent.get(key) ?? []), category]);
  }
  const build = (parent: string | null): CategoryNode[] =>
    (byParent.get(parent) ?? [])
      .slice()
      .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder) || a.name.localeCompare(b.name, 'fa'))
      .map((category) => ({ category, children: build(category.id) }));
  return build(null);
}

/** مسیر نام دسته از ریشه. */
export function categoryPath(categories: readonly CategoryView[], id: string | null | undefined): string[] {
  const byId = new Map(categories.map((category) => [category.id, category]));
  const names: string[] = [];
  let current = id ? byId.get(id) : undefined;
  while (current && names.length < 4) {
    names.unshift(current.name);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return names;
}

export interface SpecialtyOption {
  key: string;
  name: string;
  isSample: boolean;
}

function specialtyName(item: ReferenceItem): string {
  const data = item.data as { name_fa?: unknown; name?: unknown } | null;
  const name = data?.name_fa ?? data?.name;
  return typeof name === 'string' ? name : item.key;
}

/** تخصص‌ها از داده مرجع MEDICAL_SPECIALTIES (IsSample ← نشان «نمونه» — CLAUDE.md §10). */
export function useSpecialties() {
  const { tenantApi } = useTenantSession();
  const query = useQuery({
    queryKey: catKeys.specialties,
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/platform/reference-data/{dataset}', {
          params: { path: { dataset: 'MEDICAL_SPECIALTIES' }, query: { limit: 200 } },
        }),
      ),
    staleTime: 60 * 60_000,
  });
  const options = useMemo<SpecialtyOption[]>(
    () =>
      (query.data?.items ?? []).map((item) => ({
        key: item.key,
        name: specialtyName(item),
        isSample: item.isSample,
      })),
    [query.data],
  );
  return { options, isSample: options.some((option) => option.isSample), isPending: query.isPending };
}

export interface ProviderOption {
  id: string;
  name: string;
}

/** ارائه‌دهندگان IAM برای استثنای مدت و قیمت ویژه (فقط با iam.member.view؛ در غیر این صورت شناسه نمایش داده می‌شود). */
export function useProviders() {
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const enabled = access.can('iam.member.view');
  const query = useQuery({
    queryKey: catKeys.providers(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/identity/providers')),
    enabled,
  });
  const options = useMemo<ProviderOption[]>(
    () =>
      (query.data?.items ?? []).map((provider) => ({ id: provider.providerId, name: provider.displayName })),
    [query.data],
  );
  return {
    options,
    name: (id: string | null | undefined) =>
      id ? (options.find((option) => option.id === id)?.name ?? id) : '',
  };
}

const STATUS_TONE: Record<ServiceStatus, StatusTone> = {
  Draft: 'pending',
  Active: 'active',
  Inactive: 'inactive',
};

/** پروفایل‌های مالیاتی مستأجر. */
export function useTaxProfiles() {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: catKeys.taxProfiles(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/tax-profiles')),
  });
}

/** فهرست‌های قیمت (پایه و شعب). */
export function usePriceLists() {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: catKeys.priceLists(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/price-lists')),
  });
}

/** گروه‌های ارائه‌دهنده قیمت. */
export function useProviderGroups() {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: catKeys.groups(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/provider-groups')),
  });
}

/** برچسب‌های منبع ORG (با org.resource.view؛ در غیر این صورت ورود آزاد کد). */
export function useResourceTags() {
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const enabled = access.can('org.resource.view');
  const query = useQuery({
    queryKey: catKeys.tags(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/resource-tags')),
    enabled,
  });
  return { tags: query.data?.items ?? [], enabled };
}

/** شعب PLT (با plt.branch.view). */
export function useBranches() {
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const enabled = access.can('plt.branch.view');
  const query = useQuery({
    queryKey: catKeys.branches(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/branches')),
    enabled,
  });
  return {
    branches: query.data?.items ?? [],
    name: (id: string | null | undefined) =>
      id ? (query.data?.items.find((branch) => branch.id === id)?.name ?? id) : '',
  };
}

/** وضعیت خدمت با رنگ ثابت. */
export function ServiceStatusChip({ status }: { status: ServiceStatus }) {
  return <StatusChip tone={STATUS_TONE[status]} labelKey={`cat:status.${status}`} />;
}

/** نشان دسته حساس (AC-CAT-001.3؛ «از والد» برای علامت موروثی). */
export function SensitiveBadge({ inherited = false }: { inherited?: boolean }) {
  const { t } = useTranslation('cat');
  return (
    <Tooltip title={t('cat:sensitive.tooltip')}>
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        icon={<ShieldOutlinedIcon />}
        label={t(inherited ? 'cat:sensitive.inherited' : 'cat:sensitive.badge')}
        data-testid="sensitive-badge"
      />
    </Tooltip>
  );
}

/** امروز به‌صورت تاریخ کسب‌وکاری yyyy-MM-dd در منطقه زمانی مستأجر. */
export function todayIn(timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const value = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
}

/** پیام موفقیت ذخیره در تب‌های SCR-STAFF-201. */
export function SavedNotice({ show }: { show: boolean }) {
  const { t } = useTranslation('cat');
  return show ? (
    <Alert severity="success" data-testid="save-success">
      {t('cat:detail.saved')}
    </Alert>
  ) : null;
}
